import { put, takeEvery, select, call } from 'redux-saga/effects';
import { startSubmit, stopSubmit } from 'redux-form';
import { notificationsAdd } from 'virtues-bootstrap';
import { getUniqId } from 'utilities';
import { push } from 'connected-react-router';
import moment from 'moment';
import ReactGA from 'react-ga';

import { TIMING_CATEGORIES, TIMING_VARS } from 'services/googleAnalytics';

import i18n from 'services/i18n';
import { FORM_NAMES } from 'constants/forms';

import {
  parsePdf,
  requestParsePdf,
  reportCreate,
  storePdfReport,
  requestReportCreate,
  requestReportCreateFailure,
  requestSendEmailDoctorNotification,
} from 'store/actionCreators/newReport';

import {
  requestUploadDocumentReference,
  requestUploadDocumentReferenceBatch,
} from 'store/actionCreators/newCardioReport';
import {
  getDeviceVendor,
  getCreatedReportData,
  getPdfReportFileObject,
  getChoosenDoctorId,
} from 'store/selectors/newReport';
import { getPatientPageUrlId } from 'store/selectors/router';
import { uploadFile } from 'utilities/uploadFile';
import { createSagaRace } from 'utilities/request';
import { createReportObservationBody } from 'utilities/fhir/observation';
import { requestReports } from 'store/actionCreators/reports/recent';
import { getUserId } from 'store/selectors/user';
import { getAdditionalTransmissionReportDocs } from 'store/selectors/form';

export function* onParsePdf(action) {
  const { file } = action.payload;
  const type = yield select(getDeviceVendor);

  const formData = new FormData();
  formData.set('pdfFile', file);
  formData.append('pdfType', type);
  // console.log(file)
  // const [{ data }] = yield call(uploadFile, [file]);
  // TODO Should remove base64 data from store
  yield put(storePdfReport(file));
  yield put(requestParsePdf({ formData }));
}

export function* onReportCreate({ payload: { initTime } }) {
  try {
    yield put(startSubmit(FORM_NAMES.transmissionReportSummary)); // will clear errors
    const patientId = yield select(getPatientPageUrlId);
    const performer = yield select(getUserId);
    const attachedDocs = yield select(getAdditionalTransmissionReportDocs);

    const parsedAttachedDocs = yield call(uploadFile, attachedDocs);

    const doctorId = yield select(getChoosenDoctorId);
    const { componentData, practitioners } = yield select(getCreatedReportData);

    // TODO Remove pdf source from store
    const pdfFileObjectReport = yield select(getPdfReportFileObject);
    const [pdfSource] = yield call(uploadFile, [pdfFileObjectReport]);

    yield put(
      requestUploadDocumentReferenceBatch({
        docs: parsedAttachedDocs,
        patientId,
        author: practitioners,
        codes: ['additionalDocForReport'],
      }),
    );

    const { success: batchUploadSuccess, failure: batchUploadFailure } = yield createSagaRace(
      requestUploadDocumentReferenceBatch,
    );

    if (batchUploadFailure) {
      yield put(
        notificationsAdd({
          id: getUniqId(),
          variant: 'danger',
          title: i18n.t('common:requestError'),
          description: 'Problem with uploading additional docs',
        }),
      );
    }

    yield put(
      requestUploadDocumentReference({
        file: { data: pdfSource.data, type: 'application/pdf' },
        patientId,
        author: practitioners,
        codes: ['transmissionReportFile'],
      }),
    );

    const {
      success: transmissionReportUploadSuccess,
      failure: transmissionReportUploadFailure,
    } = yield createSagaRace(requestUploadDocumentReference);

    if (transmissionReportUploadFailure) {
      yield put(stopSubmit(FORM_NAMES.transmissionReportSummary));
      return;
    }

    const { payload: additionalDocsList } = batchUploadSuccess;
    const { payload: transmissionReportData } = transmissionReportUploadSuccess;

    const observationBody = createReportObservationBody({
      patientId,
      practitioners,
      componentData,
      reportId: transmissionReportData.response.id,
      docs: additionalDocsList.response,
    });

    yield put(requestReportCreate({ data: observationBody }));

    const { failure: failureReportCreate } = yield createSagaRace(requestReportCreate);

    if (failureReportCreate) {
      yield put(stopSubmit(FORM_NAMES.transmissionReportSummary));
      return;
    }
    yield put(stopSubmit(FORM_NAMES.transmissionReportSummary));

    yield put(requestReports({ patientId, performer }));

    ReactGA.timing({
      category: TIMING_CATEGORIES.forms,
      variable: TIMING_VARS.addReport,
      value: moment().diff(initTime),
    });

    yield put(requestSendEmailDoctorNotification({ patientId, doctorId }));

    yield put(push(`/patient/${patientId}/transmission-report`));
  } catch (error) {
    yield put(requestReportCreateFailure(error));
  }
}

export default function*() {
  yield takeEvery(parsePdf, onParsePdf);
  yield takeEvery(reportCreate, onReportCreate);
}
